import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IElmServiceTaskMetadataUpdate } from '../interfaces/employee-lifecycle/elm-service-task-metadata-update.interface';
import { IElmServiceTask } from '../interfaces/employee-lifecycle/elm-service-task.interface';
import { ElmServiceTaskStatus } from '../interfaces/employee-lifecycle/elm-service-task-status.enum';
import { IPaginatedResponse } from '../interfaces/IPaginatedResponse';
import { IPaginationProps } from '../interfaces/IPaginationProps';
import { PaginationOperations } from '../operations/pagination.operations';
import { IElmServiceTaskFilterEmployee } from '../interfaces/employee-lifecycle/elm-service-task-filter-employee.interface';
import { IElmServiceTaskStatusCounts } from '../interfaces/employee-lifecycle/elm-service-task-status-counts.interface';

@Injectable({
  providedIn: 'root',
})
export class ElmServiceTaskService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}ElmServiceTasks`;

  public getBoard(
    paginationProps: IPaginationProps,
    assignedToMe: boolean,
    includeCompleted = false,
  ): Observable<IPaginatedResponse<IElmServiceTask>> {
    const params = this.getBoardQueryParameters(paginationProps, assignedToMe, includeCompleted);
    return this.http.get<IPaginatedResponse<IElmServiceTask>>(`${this.baseUrl}/GetBoard`, { params });
  }

  public getBoardStatusCounts(
    paginationProps: IPaginationProps,
    assignedToMe: boolean,
    includeCompleted = false,
  ): Observable<IElmServiceTaskStatusCounts> {
    const params = this.getBoardQueryParameters(paginationProps, assignedToMe, includeCompleted);
    return this.http.get<IElmServiceTaskStatusCounts>(`${this.baseUrl}/GetBoardStatusCounts`, { params });
  }

  public getById(taskId: number): Observable<IElmServiceTask> {
    return this.http.get<IElmServiceTask>(`${this.baseUrl}/GetById/${taskId}`);
  }

  public getFilterEmployees(): Observable<IElmServiceTaskFilterEmployee[]> {
    return this.http.get<IElmServiceTaskFilterEmployee[]>(`${this.baseUrl}/GetFilterEmployees`);
  }

  public getFilterAssignees(): Observable<IElmServiceTaskFilterEmployee[]> {
    return this.http.get<IElmServiceTaskFilterEmployee[]>(`${this.baseUrl}/GetFilterAssignees`);
  }

  public claim(taskId: number): Observable<IElmServiceTask> {
    return this.http.post<IElmServiceTask>(`${this.baseUrl}/Claim/${taskId}`, null);
  }

  public unclaim(taskId: number): Observable<IElmServiceTask> {
    return this.http.post<IElmServiceTask>(`${this.baseUrl}/Unclaim/${taskId}`, null);
  }

  public assign(taskId: number, assignedToId: number): Observable<IElmServiceTask> {
    return this.http.post<IElmServiceTask>(`${this.baseUrl}/Assign/${taskId}`, null, {
      params: { assignedToId },
    });
  }

  public updateMetadata(taskId: number, payload: IElmServiceTaskMetadataUpdate): Observable<IElmServiceTask> {
    return this.http.put<IElmServiceTask>(`${this.baseUrl}/Update/${taskId}`, payload);
  }

  public updateNote(taskId: number, serviceWorkerNote: string | null): Observable<IElmServiceTask> {
    return this.http.patch<IElmServiceTask>(`${this.baseUrl}/UpdateNote/${taskId}`, { serviceWorkerNote });
  }

  public updateStatus(taskId: number, status: ElmServiceTaskStatus, serviceWorkerNote?: string | null): Observable<IElmServiceTask> {
    return this.http.post<IElmServiceTask>(`${this.baseUrl}/UpdateStatus/${taskId}`, { status, serviceWorkerNote });
  }

  private getBoardQueryParameters(
    paginationProps: IPaginationProps,
    assignedToMe: boolean,
    includeCompleted = false,
  ): ReturnType<typeof PaginationOperations.getPaginatedResponseQueryParameters> {
    const params = PaginationOperations.getPaginatedResponseQueryParameters(paginationProps);
    return params.set('assignedToMe', assignedToMe).set('includeCompleted', includeCompleted);
  }
}
